import { useEffect, useState } from 'react'
import { coarseLocation } from '../_coarseLocation'

// Missing edge inference must not hold the browser fallback behind a stalled
// request. These deadlines bound discovery; the GPS permission policy is unchanged.
export const IP_LOOKUP_TIMEOUT_MS = 3000
const LOOKUP_TIMEOUT_MS = 10000
const LOOKUP_URL = '/api/v2/locations/lookup.json'

export function useNearbyLocations() {
  const [nearby, setNearby] = useState(null)
  const [geoState, setGeoState] = useState('locating')
  const [locationSource, setLocationSource] = useState(null)
  // This is deliberately separate from GPS state. Auto's empty-page fallback
  // cannot decide until the initial IP/coarse discovery path has either found
  // a location or exhausted its bounded attempts; a denied GPS prompt says
  // nothing about that request.
  const [initialDiscoveryState, setInitialDiscoveryState] = useState('pending')

  useEffect(() => {
    let active = true
    let gpsAccepted = false
    const requests = new Map()

    async function lookup(point, timeout = LOOKUP_TIMEOUT_MS) {
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), timeout)
      requests.set(controller, timer)
      try {
        const url = point ? `${LOOKUP_URL}?lat=${point.lat}&lng=${point.lng}` : LOOKUP_URL
        const response = await fetch(url, { signal: controller.signal, cache: 'no-store' })
        if (!response.ok) throw new Error('Nearby lookup failed')
        return await response.json()
      } finally {
        clearTimeout(timer)
        requests.delete(controller)
      }
    }

    function accept(data, source) {
      if (!active || (gpsAccepted && source !== 'gps')) return
      if (source === 'gps') gpsAccepted = true
      setNearby(data)
      setLocationSource(source)
      setGeoState('done')
    }

    async function discover() {
      try {
        try {
          const data = await lookup(null, IP_LOOKUP_TIMEOUT_MS)
          if (!active || gpsAccepted) return
          if (data.location_source === 'ip') {
            accept(data, 'ip')
            return
          }
        } catch {
          // No IP result, including an unavailable edge or direct-origin request.
        }
        if (!active || gpsAccepted) return
        const coarse = coarseLocation()
        if (!coarse) return
        try {
          accept(await lookup(coarse), coarse.source)
        } catch {
          // Discovery is optional; preserve a successful GPS result/error state.
        }
      } finally {
        if (active) setInitialDiscoveryState('complete')
      }
    }
    discover()

    const failGps = (state) => {
      if (active) setGeoState((previous) => (previous === 'done' ? previous : state))
    }
    if (!navigator.geolocation) {
      failGps('unsupported')
    } else {
      navigator.geolocation.getCurrentPosition(
        async ({ coords: { latitude, longitude } }) => {
          if (!active) return
          try {
            accept(await lookup({ lat: latitude, lng: longitude }), 'gps')
          } catch {
            // A failed GPS lookup must not erase or block a usable IP/coarse list.
            failGps('error')
          }
        },
        (error) => failGps(error?.code === 1 ? 'denied' : 'error'),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 300000 },
      )
    }

    return () => {
      active = false
      for (const [controller, timer] of requests) {
        clearTimeout(timer)
        controller.abort()
      }
      requests.clear()
    }
  }, [])

  return { nearby, geoState, locationSource, initialDiscoveryState }
}
